import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase_client';
import { useNavigate } from 'react-router-dom';

type LinkState = 'checking' | 'valid' | 'expired' | 'session_expired';

function parseAuthRedirectError(): { code?: string; description?: string } | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const error = search.get('error') || hash.get('error');
  const errorCode = search.get('error_code') || hash.get('error_code');
  const description = search.get('error_description') || hash.get('error_description');
  if (!error && !errorCode) return null;
  return {
    code: errorCode || error || undefined,
    description: description
      ? decodeURIComponent(description.replace(/\+/g, ' '))
      : undefined,
  };
}

function isDeadResetLink(code?: string, description?: string): boolean {
  const hay = `${code || ''} ${description || ''}`.toLowerCase();
  return (
    hay.includes('otp_expired') ||
    hay.includes('expired') ||
    hay.includes('access_denied') ||
    hay.includes('otp_disabled') ||
    hay.includes('invalid')
  );
}

function isMissingSessionError(message?: string): boolean {
  const hay = (message || '').toLowerCase();
  return hay.includes('auth session missing') || hay.includes('session') && hay.includes('expired');
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkState, setLinkState] = useState<LinkState>('checking');

  useEffect(() => {
    const urlError = parseAuthRedirectError();
    // Circumstance 1: they clicked after the one-time link was already dead.
    // Supabase redirects here with error=access_denied&error_code=otp_expired
    // and never creates a session.
    if (urlError && isDeadResetLink(urlError.code, urlError.description)) {
      setLinkState('expired');
      setMsg('This reset link has expired or was already used. Please request a new password reset.');
      return;
    }

    let gotRecoverySession = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === 'PASSWORD_RECOVERY' ||
        (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED'))
      ) {
        gotRecoverySession = true;
        setLinkState('valid');
        setMsg(null);
        return;
      }

      // Circumstance 2: they opened a valid link, then sat on this page until
      // the recovery session expired.
      if (event === 'SIGNED_OUT' && gotRecoverySession) {
        setLinkState('session_expired');
        setMsg('This reset session expired because the page was left open too long. Please request a new password reset.');
      }
    });

    const timeoutId = window.setTimeout(async () => {
      if (gotRecoverySession) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        gotRecoverySession = true;
        setLinkState('valid');
        setMsg(null);
        return;
      }
      setLinkState('expired');
      setMsg('This reset link has expired or was already used. Please request a new password reset.');
    }, 2500);

    return () => {
      window.clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);

    if (password !== confirmPassword) {
      setMsg('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setMsg('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      setMsg('Password updated successfully! Redirecting to sign in...');
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (err: any) {
      const message = err?.message ?? 'An error occurred. Please try again.';
      if (isMissingSessionError(message)) {
        setLinkState('session_expired');
        setMsg('This reset session expired. Please request a new password reset.');
      } else {
        setMsg(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const goRequestNewLink = () => navigate('/Forgot-Password');

  const expiredCopy =
    linkState === 'session_expired'
      ? {
          title: 'Reset session expired',
          body: 'You opened a valid reset link, but this page was left open too long and the session expired. Request a new password reset email and use the new link promptly.',
        }
      : {
          title: 'Reset link expired',
          body: 'This password reset link is invalid, expired, or was already used. Request a new password reset email and click the latest link.',
        };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8">
        {linkState === 'checking' && (
          <>
            <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
              Reset Password
            </h2>
            <p className="text-center text-gray-500">Verifying reset link...</p>
          </>
        )}

        {(linkState === 'expired' || linkState === 'session_expired') && (
          <>
            <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
              {expiredCopy.title}
            </h2>
            <p className="text-center text-gray-500 mb-6">{expiredCopy.body}</p>
            {msg && (
              <div className="text-center mb-4 p-3 rounded bg-red-100 text-red-700">
                {msg}
              </div>
            )}
            <button
              type="button"
              onClick={goRequestNewLink}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors"
            >
              Request a new reset link
            </button>
          </>
        )}

        {linkState === 'valid' && (
          <>
            <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
              Reset Password
            </h2>
            <p className="text-center text-gray-500 mb-6">
              Enter your new password below
            </p>

            {msg && (
              <div
                className={`text-center mb-4 p-3 rounded ${
                  msg.includes('success') || msg.includes('successfully')
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {msg}
              </div>
            )}

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label className="block text-gray-700 mb-1 font-medium">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Enter your new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 6 characters
                </p>
              </div>

              <div>
                <label className="block text-gray-700 mb-1 font-medium">
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
